import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IMAGES, getTutorialImage } from '../utils/images';

import './profile.css';
import './credits.css';
import './tutorial.css';

export interface TutorialStage {
    title: string;
    description: string;
    image: string | string[];
}

const stages: TutorialStage[] = [
    {
        title: 'Welcome to Monsters of the Sea',
        description:
            'Monsters of the Sea is a turn based strategy game where you compete to be the first player to eliminate all of your opponents monsters or reach their island.',
        image: getTutorialImage('home.jpg'),
    },
    {
        title: 'Monsters',
        description:
            'There are 10 different types of monsters each of which has its own movement pattern. A key element of the game is that you will not know what monsters your opponent has sleected, and therefore must deduce the monsters based on how they move throughout the game.',
        image: [getTutorialImage('kraken.jpg'), getTutorialImage('white_whale.jpg'), getTutorialImage('jormungandr.jpg')],
    },
    {
        title: 'Islands & Island Placement',
        description:
            'Each player will also have a chance an island on their side of the board. An island consists of 5 connected tiles and can be placed anywhere on the players side of the board. The game starts with both players placing their 5 islands and hitting submit, upon which the next phase will begin.',
        image: getTutorialImage('place_islands.jpg'),
    },
    {
        title: 'Monster Placement',
        description:
            'Now with the knowledge of their opponents island placements, the players will then take turns selecting and placing a monster on their side of the board until both players have placed 5 monsters each. Only one of each kind of monster can be placed and not all monsters will be used.',
        image: getTutorialImage('place_monsters.jpg'),
    },
    {
        title: 'Gameplay',
        description: `Once both players have placed their monsters, the game will begin. The players will then take turns moving their monsters aroudn the board. If a monster lands on a tile containing an opponent's monster, that monster will be captured and removed from the board.`,
        image: getTutorialImage('gameplay.jpg'),
    },
    {
        title: 'Deduction',
        description:
            'After your opponent moves a monster, based on its movement, you may use your deduction panel on the left to cross off monsters who would have been unable to make that move. Eventually throughout the game you will be able to deduce what monsters your opponent has.',
        image: getTutorialImage('deduction.jpg'),
    },
    {
        title: 'Winning the Game',
        description: `The game is won by either capturing all 5 of your opponent's monsters or by reaching their island first.`,
        image: getTutorialImage('game_end.jpg'),
    },
    {
        title: 'Good luck!',
        description: `That's about it! The game is best learned through playing so good luck and have fun! We hope you enjoy Monsters of the Sea!`,
        image: getTutorialImage('home.jpg'),
    },
];

function stageImages(entry: string | string[]): string[] {
    return Array.isArray(entry) ? entry : [entry];
}

const TutorialPage: React.FC = () => {
    const navigate = useNavigate();
    const [index, setIndex] = useState(0);
    const last = stages.length - 1;
    const canPrev = index > 0;
    const canNext = index < last;
    const stage = stages[index];
    const images = stageImages(stage.image);

    const goPrev = useCallback(() => {
        setIndex((i) => (i > 0 ? i - 1 : i));
    }, []);
    const goNext = useCallback(() => {
        setIndex((i) => (i < last ? i + 1 : i));
    }, [last]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft' && canPrev) {
                e.preventDefault();
                goPrev();
            } else if (e.key === 'ArrowRight' && canNext) {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [canPrev, canNext, goPrev, goNext]);

    return (
        <div
            className="profile_page_container"
            style={{ backgroundImage: `url(${IMAGES.backgrounds.room})` }}
        >
            <header className="profile_top_bar credits_top_bar">
                <button type="button" className="button_std credits_top_bar_back" onClick={() => navigate('/home')}>
                    Back
                </button>
                <h1 className="credits_top_title">Tutorial</h1>
                <img src={IMAGES.logo} alt="" className="profile_top_bar_logo credits_top_bar_logo" />
            </header>
            <div className="credits_main tutorial_main" aria-label="Tutorial body">
                <div className="tutorial_stage_row" role="region" aria-label={`Step ${index + 1} of ${stages.length}`}>
                    <button
                        type="button"
                        className="button_std tutorial_nav_arrow"
                        onClick={goPrev}
                        disabled={!canPrev}
                        aria-label="Previous step"
                    >
                        ←
                    </button>
                    <div className="tutorial_stage" aria-live="polite">
                        <h2 className="tutorial_stage_title">{stage.title}</h2>
                        <div
                            className={
                                index % 2 === 0
                                    ? 'tutorial_stage_body'
                                    : 'tutorial_stage_body tutorial_stage_body--image-right'
                            }
                        >
                            <div className="tutorial_stage_media">
                                {images.map(
                                    (src, i) =>
                                        src && (
                                            <div key={`${index}-${i}`} className="tutorial_stage_media_slot">
                                                <img src={src} alt="" />
                                            </div>
                                        ),
                                )}
                            </div>
                            <p className="tutorial_stage_text">{stage.description}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="button_std tutorial_nav_arrow"
                        onClick={goNext}
                        disabled={!canNext}
                        aria-label="Next step"
                    >
                        →
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TutorialPage;
